<template>
  <nav class="fixed left-0 top-0 bottom-0 w-64 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-r border-gray-200 dark:border-gray-800 z-30 overflow-y-auto">
    <!-- Header with Logo and Toggle -->
    <div class="p-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <!-- Mobile menu button - Only show when authenticated and on mobile -->
          <button
            v-if="authStore.isAuthenticated && isMobile"
            type="button"
            class="inline-flex items-center justify-center rounded-md bg-gray-200 dark:bg-gray-800 p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 touch-manipulation"
            aria-controls="mobile-menu"
            aria-expanded="false"
            @click="$emit('toggle-mobile-menu')"
          >
            <span class="sr-only">{{ t('app.actions.open_main_menu') }}</span>
            <Bars3Icon
              v-if="!isMobileMenuOpen"
              class="block h-6 w-6"
              aria-hidden="true"
            />
            <XMarkIcon
              v-else
              class="block h-6 w-6"
              aria-hidden="true"
            />
          </button>
          
          <NavbarLogo
            size="small"
            :show-restaurant-name="false"
          />
        </div>
        
        <!-- Navbar position toggle - Only show on desktop -->
        <button
          v-if="!isMobile"
          type="button"
          class="hidden md:inline-flex items-center justify-center rounded-md bg-gray-200 dark:bg-gray-800 p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 touch-manipulation"
          :title="isVertical ? 'Cambiar a navbar horizontal' : 'Cambiar a navbar vertical'"
          @click="$emit('toggle-navbar-position')"
        >
          <span class="sr-only">Cambiar posición del navbar</span>
          <ViewColumnsIcon
            v-if="isHorizontal"
            class="block h-5 w-5"
            aria-hidden="true"
          />
          <Bars2Icon
            v-else
            class="block h-5 w-5"
            aria-hidden="true"
          />
        </button>
      </div>
      
      <!-- Restaurant Name -->
      <div
        v-if="restaurant"
        class="mt-2"
      >
        <span class="text-xs font-semibold text-gray-900 dark:text-white truncate">
          {{ restaurant.name }}
        </span>
      </div>
    </div>

    <!-- Navigation Links -->
    <NavbarNavigation layout="vertical" />

    <!-- User Section -->
    <UserMenu 
      layout="vertical" 
      :unread-alert-count="unreadAlertCount"
      @notification-click="$emit('notification-click')"
      @logout="$emit('logout')"
    />
  </nav>
</template>

<script setup lang="ts">
import { Bars3Icon, XMarkIcon, ViewColumnsIcon, Bars2Icon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { useRestaurant } from '@/composables/useRestaurant';
import NavbarLogo from './NavbarLogo.vue';
import NavbarNavigation from './NavbarNavigation.vue';
import UserMenu from './UserMenu.vue';

interface Props {
  isMobile?: boolean;
  isMobileMenuOpen?: boolean;
  isVertical?: boolean;
  isHorizontal?: boolean;
  unreadAlertCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isMobile: false,
  isMobileMenuOpen: false,
  isVertical: true,
  isHorizontal: false,
  unreadAlertCount: 0
});

const emit = defineEmits<{
  'toggle-mobile-menu': [];
  'toggle-navbar-position': [];
  'notification-click': [];
  'logout': [];
}>();

const authStore = useAuthStore();
const { t } = useI18n();
const { restaurant } = useRestaurant();
</script>
